// What a row of «Настройки» shows, apart from React (DESIGN.md «Настройки»): which control a setting gets, its default in words,
// when it is off because of another setting, which exceptions may be added, and what the search of «Настройки» finds.

import type { Id, SettingDecl, SettingScope, SettingValue } from '@confdeck/core-shared';

/** A row of segments holds at most this many options; a longer choice is a list (`control: 'select'`). */
export const SEGMENTS_MAX = 5;

export type Control = 'switch' | 'number' | 'segments' | 'select' | 'text' | 'several';
/** The control of a setting: by its type and the declared `control`, never by the count of options (DESIGN.md «Контролы»). */
export const controlOf = (d: SettingDecl): Control =>
  d.type === 'boolean' ? 'switch' : d.type === 'number' ? 'number' : d.type === 'text' ? 'text' : d.multiple ? 'several' : d.control === 'select' ? 'select' : 'segments';

/** A value in words: «включено», «60 мин», «Москва (UTC+3)», «Выручка, Участники»; an empty text — «не задано». */
export function valueText(d: SettingDecl, v: SettingValue): string {
  if (d.type === 'boolean') return v ? 'включено' : 'выключено';
  if (d.type === 'number') return `${v}${d.unit ? ` ${d.unit}` : ''}`;
  if (d.type === 'text') return String(v).trim() ? `«${String(v)}»` : 'не задано';
  if (d.multiple) { const keys = new Set(String(v).split(',')); return (d.options ?? []).filter((o) => keys.has(o.value)).map((o) => o.label).join(', ') || 'ничего'; }
  return d.options?.find((o) => o.value === v)?.label ?? String(v);
}

/** The scopes an exception on «Настройки» may take: a role or a person. A value of a record lives on that record's tab (R-E7). */
export const exceptionScopes = (d: SettingDecl): SettingScope[] => d.scopes.filter((x) => x === 'role' || x === 'user');

/** «По умолчанию — …», said in the explanation of the row while nobody has set a value for everyone. */
export const defaultText = (d: SettingDecl) => { const v = valueText(d, d.default); return `По умолчанию — ${v}${v.endsWith('.') ? '' : '.'}`; };

/**
 * Why the row is off: the setting it depends on has another value for everyone (B9). `forAll` — the value for everyone of a key
 * (the default when nobody set one).
 */
export function offReason(d: SettingDecl, decls: SettingDecl[], forAll: (key: string) => SettingValue | undefined): string | null {
  if (!d.when) return null;
  const main = decls.find((x) => x.key === d.when!.key);
  if (!main || forAll(main.key) === d.when.value) return null;
  return `Действует, когда «${main.name}» — ${valueText(main, d.when.value).toLowerCase()}.`;
}

/** Who an exception may be for: a role or a person; «Все» only to give one section its own value (B8). */
export function exceptionWho(d: SettingDecl, roles: { id: Id; name: string }[], people: { id: Id; name: string }[]) {
  return [
    ...(d.perType && d.scopes.includes('all') ? [{ id: 'all:', label: 'Все', group: 'Кому' }] : []),
    ...(d.scopes.includes('role') ? roles.map((r) => ({ id: `role:${r.id}`, label: r.name, group: 'Роли' })) : []),
    ...(d.scopes.includes('user') ? people.map((u) => ({ id: `user:${u.id}`, label: u.name, group: 'Люди' })) : []),
  ];
}

/** The sections of an exception; for «Все» there is no «Все разделы» — that is the value for everyone, not an exception (B8). */
export function exceptionSections(scope: SettingScope, types: { id: Id; nameMany: string }[]) {
  return [...(scope === 'all' ? [] : [{ id: '', label: 'Все разделы' }]), ...types.map((t) => ({ id: t.id, label: t.nameMany }))];
}

/** One thing the search of «Настройки» finds: an item, or a setting inside a group's item. */
export interface SettingsHit { item: string; itemLabel: string; key?: string; label: string; hint: string }

const norm = (x: string) => x.toLowerCase().replace(/ё/g, 'е');

/**
 * The search of «Настройки» (decision 038 п.3): items by their name, settings by the name, the explanation and the names of
 * their options. Every word of the query must be found; a match in the name goes first.
 */
export function searchSettings(query: string, items: { key: string; label: string; group?: string; words?: string }[], decls: SettingDecl[]): SettingsHit[] {
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const has = (text: string) => { const t = norm(text); return words.every((w) => t.includes(w)); };
  const out: (SettingsHit & { rank: number })[] = [];
  for (const i of items) {
    if (has(`${i.label} ${i.words ?? ''}`)) out.push({ item: i.key, itemLabel: i.label, label: i.label, hint: 'Настройки', rank: has(i.label) ? 0 : 2 });
    if (i.group === undefined) continue;
    for (const d of decls.filter((x) => x.group === i.group)) {
      const all = `${d.name} ${d.description} ${(d.options ?? []).map((o) => o.label).join(' ')} ${i.label}`;
      if (has(all)) out.push({ item: i.key, itemLabel: i.label, key: d.key, label: d.name, hint: `Настройки · ${i.label}`, rank: has(d.name) ? 1 : 3 });
    }
  }
  return out.sort((a, b) => a.rank - b.rank).map(({ rank: _, ...h }) => h);
}
